import { isEmpty } from '@ember/utils';
import { BLUE_500, CLEMENTINE_500, RASPBERRY_500, TEAL_500 } from 'cio/utils/colors';
import { ActionType } from 'cio/models/action';
import CampaignModel from 'cio/models/campaign';
import memoize from 'lodash.memoize';

export type ActionInfo = {
  actionType: ActionType;
  name: string;
  analyticsName: string;
  shortName: string;
  addActionsPanelName?: (campaign?: CampaignModel) => string;
  activePhrase?: string;
  activePhraseShort?: string;
  icon: string;
  group?: string;
  beta?: boolean;
  betaTooltip?: string;
  betaAcceptFeedback?: boolean;
  nameRequired?: boolean;
  nameOptional?: boolean;
  deliveryActions?: string[] | undefined;
  deliveryMetrics?: string[] | undefined;
  deliveryStates?: string[] | undefined;
  segmentFilters?: string[] | undefined;
  optionallyConverts?: boolean;
  isPremiumFeature?: boolean;
  enableByDefault?: boolean;
  [key: string]: string | string[] | number | boolean | undefined | (() => unknown);
};

export type ConditionListEditMessageActionType = {
  value: string;
  label: string;
  icon: string;
};

export const ACTION_INFO: { [key in ActionType]?: ActionInfo } = {
  // Wait until... action
  conditional_wait_action: {
    actionType: 'conditional_wait_action',
    name: 'Wait Until…',
    analyticsName: 'Wait Until',
    shortName: 'wait until',
    activePhrase: 'wait',
    activePhraseShort: 'wait',
    icon: 'wait',
    group: 'delays',
    nameRequired: false,
  },

  // Delays
  delay_seconds_action: {
    actionType: 'delay_seconds_action',
    name: 'Time Delay',
    analyticsName: 'Time Delay',
    shortName: 'time delay',
    activePhrase: 'wait in this delay',
    activePhraseShort: 'wait',
    icon: 'time-delay',
    group: 'delays',
    nameRequired: false,
  },

  delay_time_window_action: {
    actionType: 'delay_time_window_action',
    name: 'Time Window',
    analyticsName: 'Time Window',
    shortName: 'time window',
    activePhrase: 'wait for this time window',
    activePhraseShort: 'wait',
    icon: 'time-window',
    group: 'delays',
    nameRequired: false,
  },

  grace_period_action: {
    actionType: 'grace_period_action',
    name: 'Grace Period',
    analyticsName: 'Grace Period',
    shortName: 'grace period',
    activePhrase: 'wait in this grace period',
    activePhraseShort: 'wait',
    icon: 'time-delay',
    group: 'delays',
    nameRequired: false,
  },

  // Messages
  email_action: {
    actionType: 'email_action',
    name: 'Email',
    analyticsName: 'Email',
    shortName: 'email',
    newActionName: 'Email',
    longPluralName: 'emails',
    activePhrase: 'send this email',
    activePhraseShort: 'send',
    placeholder: 'Welcome Email',
    icon: 'email',
    canSplit: true,
    showNoSubject: true,
    showNoContent: true,
    showTracked: true,
    showUnsubscribed: true,
    deliveryStates: [
      'attempted',
      'deferred',
      'sent',
      'failed',
      'bounced',
      'suppressed',
      'undeliverable',
      'delivered',
      'holdout',
    ],
    deliveryActions: ['opened', 'clicked', 'converted', 'unsubscribed', 'spammed', 'topic_unsubscribed'],
    deliveryMetrics: ['opened', 'clicked', 'converted', 'unsubscribed', 'drafted', 'topic_unsubscribed'],
    group: 'messages',
    nameRequired: true,
    settingsPath: 'actions.email',
  },

  slack_action: {
    actionType: 'slack_action',
    name: 'Slack Message',
    analyticsName: 'Slack',
    shortName: 'Slack message',
    newActionName: 'Slack Message',
    longPluralName: 'Slack messages',
    activePhrase: 'send this message',
    activePhraseShort: 'send',
    placeholder: 'Sales Alert',
    icon: 'slack',
    logo: new URL('cio/assets/images/actions/slack.svg', import.meta.url).toString(),
    showNoSubject: false,
    showNoContent: true,
    segmentFilters: ['sent', 'failed', 'clicked'],
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['clicked'],
    deliveryMetrics: ['drafted'],
    group: 'messages',
    nameRequired: true,
    settingsPath: 'actions.slack',
    requiresSetup: true,
  },

  twilio_action: {
    actionType: 'twilio_action',
    name: 'Twilio SMS',
    analyticsName: 'Twilio',
    shortName: 'SMS',
    newActionName: 'SMS',
    longPluralName: 'SMS messages',
    activePhrase: 'send this SMS',
    activePhraseShort: 'send',
    placeholder: 'Account Activation SMS',
    icon: 'sms',
    canSplit: true,
    logo: new URL('cio/assets/images/actions/twilio.svg', import.meta.url).toString(),
    showNoSubject: false,
    showNoContent: true,
    showUnsubscribed: true,
    segmentFilters: ['sent', 'failed', 'converted', 'bounced', 'clicked', 'delivered'],
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'undeliverable', 'delivered', 'holdout'],
    deliveryActions: ['clicked', 'converted'],
    deliveryMetrics: ['converted', 'drafted'],
    group: 'messages',
    nameRequired: true,
    settingsPath: 'actions.twilio',
    requiresSetup: true,
  },

  push_action: {
    actionType: 'push_action',
    name: 'Push Notification',
    analyticsName: 'Push',
    typeName: 'notification',
    longPluralName: 'push messages',
    shortName: 'push',
    newActionName: 'Push',
    activePhrase: 'send this push',
    activePhraseShort: 'send',
    placeholder: 'Welcome Push Notification',
    icon: 'mobile',
    canSplit: true,
    showNoSubject: false,
    showNoContent: true,
    showTargetPlatform: true,
    showUnsubscribed: true,
    segmentFilters: ['sent', 'failed', 'converted', 'opened', 'bounced', 'dropped', 'undeliverable', 'delivered'],
    deliveryStates: ['attempted', 'sent', 'failed', 'bounced', 'suppressed', 'undeliverable', 'holdout', 'delivered'],
    deliveryActions: ['opened', 'converted'],
    deliveryMetrics: ['opened', 'converted', 'drafted'],
    group: 'messages',
    nameRequired: true,
    settingsPath: 'actions.push',
    requiresSetup: true,
    enableByDefault: true,
  },

  urban_airship_action: {
    actionType: 'urban_airship_action',
    name: 'Urban Airship',
    analyticsName: 'Urban Airship',
    typeName: 'push',
    shortName: 'UA push',
    newActionName: 'UA Push',
    longPluralName: 'Urban Airship push messages',
    activePhrase: 'send this UA push',
    activePhraseShort: 'send',
    placeholder: 'Welcome Push Notification',
    icon: 'mobile',
    canSplit: true,
    logo: new URL('cio/assets/images/actions/urban-airship.svg', import.meta.url).toString(),
    showNoSubject: false,
    showNoContent: true,
    showUnsubscribed: true,
    segmentFilters: ['sent', 'failed', 'converted', 'clicked'],
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['clicked', 'converted'],
    deliveryMetrics: ['converted', 'drafted'],
    group: 'messages',
    nameRequired: true,
    hideIfDisabled: true,
    settingsPath: 'actions.urban_airship',
  },

  in_app_action: {
    actionType: 'in_app_action',
    analyticsName: 'In-App',
    activePhrase: 'send this message',
    activePhraseShort: 'send',
    deliveryStates: ['attempted', 'sent', 'failed', 'holdout'],
    deliveryActions: ['opened', 'clicked', 'converted'],
    deliveryMetrics: ['drafted', 'opened', 'clicked', 'converted'],
    group: 'messages',
    icon: 'in-app',
    hideIfDisabled: false,
    enableByDefault: true,
    longPluralName: 'In-app messages',
    placeholder: 'Welcome Message',
    name: 'In-App Message',
    nameRequired: true,
    newActionName: 'In-App Message',
    requiresSetup: true,
    settingsPath: 'actions.in_app',
    shortName: 'in-app message',
    showNoContent: true,
    showNoSubject: false,
    segmentFilters: ['sent', 'failed', 'clicked', 'converted', 'opened'],
  },

  // Others
  webhook_action: {
    actionType: 'webhook_action',
    name: 'Webhook',
    addActionsPanelName(campaign?: CampaignModel) {
      if (campaign?.isWebhookTriggered) {
        return 'Send Data';
      }

      return 'Send and Receive Data';
    },
    analyticsName: 'Webhook',
    shortName: 'webhook',
    newActionName: 'Webhook',
    longPluralName: 'webhooks',
    activePhrase: 'send this webhook',
    activePhraseShort: 'send',
    placeholder: 'New Signup Notification',
    icon: 'webhook',
    showRequest: true,
    segmentFilters: ['sent', 'failed', 'clicked'],
    deliveryStates: ['attempted', 'sent', 'failed', 'undeliverable', 'holdout'],
    deliveryActions: ['clicked'],
    deliveryMetrics: ['drafted'],
    group: 'data',
    nameRequired: true,
    optionallyConverts: true,
  },

  attribute_update_action: {
    actionType: 'attribute_update_action',
    name: 'Create or Update Person',
    analyticsName: 'Create or Update Person',
    shortName: 'create or update person',
    newActionName: 'Create or Update Person',
    editSidebarName: 'Create or Update Person',
    placeholder: "Update Customer's Anniversary",
    activePhrase: 'create or update person',
    activePhraseShort: 'create or update',
    icon: 'person',
    showNoContent: true,
    group: 'data',
    nameRequired: true,
  },

  static_seg_update_action: {
    actionType: 'static_seg_update_action',
    name: 'Manual Segment Update',
    analyticsName: 'Manual Segment Update',
    shortName: 'segment update',
    activePhrase: 'update segment',
    activePhraseShort: 'update',
    icon: 'segment-manual',
    group: 'data',
    nameRequired: false,
    nameOptional: true,
  },

  collection_query_action: {
    actionType: 'collection_query_action',
    name: 'Query Collection',
    analyticsName: 'Query Collection Update',
    shortName: 'query collection',
    activePhrase: 'update collection',
    activePhraseShort: 'update',
    icon: 'collection',
    group: 'data',
    nameRequired: false,
    nameOptional: true,
    showNoQuery: true,
    isPremiumFeature: true,
  },

  create_event_action: {
    actionType: 'create_event_action',
    name: 'Send Event',
    analyticsName: 'Create Event',
    shortName: 'event',
    newActionName: 'Event',
    activePhrase: 'send event',
    activePhraseShort: 'send',
    icon: 'event',
    group: 'data',
    nameRequired: true,
  },

  batch_update_action: {
    actionType: 'batch_update_action',
    name: 'Batch Update',
    analyticsName: 'Batch Update',
    shortName: 'update',
    newActionName: 'Batch Update',
    activePhrase: 'update',
    activePhraseShort: 'update',
    icon: 'person',
    group: 'data',
    nameRequired: true,
  },

  // Tests
  split_randomized_action: {
    actionType: 'split_randomized_action',
    name: 'A/B Test',
    analyticsName: 'A/B Test',
    shortName: 'test',
    editSidebarName: 'Edit A/B Test',
    activePhrase: 'test and send',
    activePhraseShort: 'test and send',
    icon: 'a-b-test',
    nameRequired: false,
  },

  // Branches
  conditional_branch_action: {
    actionType: 'conditional_branch_action',
    name: 'True/False Branch',
    analyticsName: 'TF Branch',
    shortName: 'True/False Branch',
    icon: 'branch-two',
    group: 'flow_control',
    nameOptional: true,
  },

  multi_split_branch_action: {
    actionType: 'multi_split_branch_action',
    name: 'Multi-Split Branch',
    analyticsName: 'Multi-Split Branch',
    shortName: 'Multi-Split Branch',
    icon: 'branch-three',
    group: 'flow_control',
    nameOptional: true,
  },

  multi_lang_branch_action: {
    actionType: 'multi_lang_branch_action',
    name: 'Multi-Language Branch',
    analyticsName: 'Multi-Lang Branch',
    shortName: 'Multi-Lang Branch',
    icon: 'localization',
    group: 'flow_control',
    nameOptional: true,
  },

  random_cohort_branch_action: {
    actionType: 'random_cohort_branch_action',
    name: 'Random Cohort Branch',
    analyticsName: 'Random Cohort Branch',
    shortName: 'Random Cohort Branch',
    newActionName: 'Random Cohort Branch',
    icon: 'random-cohort',
    group: 'flow_control',
    nameOptional: true,
  },

  exit_action: {
    actionType: 'exit_action',
    name: 'Exit',
    analyticsName: 'Exit',
    shortName: 'Exit',
    editSidebarName: 'Delete Exit',
    icon: 'exit',
    group: 'flow_control',
  },
};

const DEFAULT: ActionInfo = {
  actionType: 'unknown' as any,
  name: 'Unknown',
  analyticsName: 'Unknown',
  shortName: 'unknown',
  icon: 'help',
};

const WORKFLOW_ITEM_NAMES: ActionType[] = [
  'email_action',
  'push_action',
  'twilio_action',
  'slack_action',
  'urban_airship_action',
  'in_app_action',
  'attribute_update_action',
  'create_event_action',
  'batch_update_action',
  'static_seg_update_action',
  'collection_query_action',
  'webhook_action',
  'conditional_wait_action',
  'delay_seconds_action',
  'delay_time_window_action',
  'conditional_branch_action',
  'multi_split_branch_action',
  'random_cohort_branch_action',
  'exit_action',
];

export const MESSAGING_TYPES: readonly ActionType[] = [
  'email_action',
  'push_action',
  'slack_action',
  'twilio_action',
  'urban_airship_action',
  'webhook_action',
  'in_app_action',
] as const;

// Actions that have a composer
export const COMPOSABLE_TYPES: readonly ActionType[] = [
  ...MESSAGING_TYPES,
  'attribute_update_action',
  'static_seg_update_action',
  'collection_query_action',
  'create_event_action',
  'batch_update_action',
] as const;

// Actions that have some sort of result (like sending an email or updating an
// attribute), i.e. non-delay or branch actions
export const EFFECT_TYPES: readonly ActionType[] = [
  ...MESSAGING_TYPES,
  'attribute_update_action',
  'static_seg_update_action',
  'collection_query_action',
  'split_randomized_action',
  'create_event_action',
  'batch_update_action',
] as const;

export const WORKFLOW_ITEMS = WORKFLOW_ITEM_NAMES.map(actionTypeItem);

export type ActionTypeItem = {
  info: ActionInfo;
  type: ActionType;
  label: string;
  enabled: boolean;
  attributes: {
    type: ActionType;
    addToSeg?: boolean;
  };
};

export function actionTypeItem(type: ActionType) {
  let info = actionInfo(type);
  let item: ActionTypeItem = {
    info,
    type,
    label: info.name,
    enabled: true,
    attributes: {
      type,
    },
  };

  if (type === 'static_seg_update_action') {
    item.attributes.addToSeg = true;
  }

  return item;
}

type GroupName = 'messages' | 'data' | 'delays' | 'flow_control';

export const GROUP_COLORS: { [key in GroupName]: string } = {
  messages: BLUE_500,
  data: RASPBERRY_500,
  delays: CLEMENTINE_500,
  flow_control: TEAL_500,
};

export const GROUP_NAMES: { [key in GroupName]: string } = {
  messages: 'Messages',
  data: 'Data',
  delays: 'Delays',
  flow_control: 'Flow Control',
};

export const GROUPED_ITEMS = ['messages', 'data', 'delays', 'flow_control'].map((value: GroupName) => {
  return {
    value,
    actionItems: WORKFLOW_ITEMS.filter((item) => item.info.group === value),
    color: GROUP_COLORS[value],
    name: GROUP_NAMES[value],
  };
});

export const DELIVERY_STATES_AND_ACTIONS_PER_TYPE = Object.entries(ACTION_INFO).reduce<Record<string, string[]>>(
  (acc, [actionType, actionInfo]) => {
    let statesAndActions = [
      ...new Set([...(actionInfo?.deliveryStates ?? []), ...(actionInfo?.deliveryActions ?? [])]),
    ];

    if (statesAndActions.includes('clicked') && statesAndActions.includes('opened')) {
      statesAndActions.push('clicked_to_opened');
    }

    acc[actionType] = statesAndActions;

    return acc;
  },
  {},
);

function _deliveryStatesAndActionsPerType(canConvert = false) {
  return Object.keys(ACTION_INFO).reduce<Record<ActionType, string[]>>((acc, actionType: ActionType) => {
    let info = actionInfo(actionType, { canConvert });

    let statesAndActions = [...new Set([...(info?.deliveryStates ?? []), ...(info?.deliveryActions ?? [])])];

    if (statesAndActions.includes('clicked') && statesAndActions.includes('opened')) {
      statesAndActions.push('clicked_to_opened');
    }

    acc[actionType] = statesAndActions;

    return acc;
  }, {} as any);
}

export const deliveryStatesAndActionsPerType = memoize(_deliveryStatesAndActionsPerType);

export function forEachSegmentable(callback: (action: unknown, info: ReturnType<typeof actionInfo>) => unknown) {
  return MESSAGING_TYPES.forEach(function (type) {
    let action = type.replace(/_action$/, '');
    let info = actionInfo(type);

    if (!isEmpty(info.segmentFilters)) {
      callback(action, info);
    }
  });
}

export function actionTypeName(type: ActionType) {
  if (COMPOSABLE_TYPES.includes(type)) {
    return type.replace(/_action$/, '');
  }

  return 'unknown';
}

export default function actionInfo(type: ActionType, options?: { canConvert: boolean }) {
  let canConvert = options?.canConvert ?? false;
  let info = { ...(ACTION_INFO[type] ?? DEFAULT) };

  if (info?.optionallyConverts && canConvert) {
    info.deliveryActions = [...new Set([...(info.deliveryActions ?? []), 'converted'])];
    info.deliveryMetrics = [...new Set([...(info.deliveryMetrics ?? []), 'converted'])];
    info.segmentFilters = [...new Set([...(info.segmentFilters ?? []), 'converted'])];
  }

  return info ?? DEFAULT;
}
